import React, { useCallback, type ComponentProps, type ReactElement } from 'react';
import { convertNodeToElement } from 'react-html-parser';

import { CoreLink } from '@uncinc/react-drupal-core';

import {
  Icon,
  isExternalLink,
  WysiwygField as RKSWysiwygField,
  TableRender,
} from '@uncinc/uncinc-react-kitchen-sink';

// Typing for the WysiwygField customElementHandler prop's node attribute.
// Its default type doesn't work correctly.
export interface WysiwygFieldDomElement {
  name: string;
  attribs: {
    src: string;
    href: string;
    [key: string]: any;
  };
  children: {
    data: string;
    [key: string]: any;
  }[];
}

const WysiwygField: React.FC<ComponentProps<typeof RKSWysiwygField>> = (props) => {
  const customElementHandler = useCallback((node: WysiwygFieldDomElement, index: number): ReactElement | void | null => {
    switch (node.name) {
      // Generally, we don't allow img tags in a WYSIWYG-field.
      // However, for showing the A11Y label, we need to make an exception in Drupal, so it's now
      // possible to add <img /> tags by using the 'Source' button in the Drupal WYSIWYG editor.
      case 'img': {
        return null;
      }

      case 'a': {
        // But, we only show that <img /> in this exact scenario:
        if (node.attribs.class === 'a11y-label-nioz' && node.children[0]?.name === 'img') {
          return (
            <CoreLink {...node.attribs}>
              <img
                src={node.children[0].attribs.src}
                alt={node.children[0].attribs.alt}
                width={node.children[0].attribs.width}
                height={node.children[0].attribs.height}
              />
            </CoreLink>
          );
        }
        let icon: string | undefined;

        if ('href' in node.attribs) {
          const { href } = node.attribs;

          if (isExternalLink(href)) {
            icon = 'arrow_outward';
          }
        }

        return (
          <CoreLink {...node.attribs}>
            {node.children[0]?.data}

            {icon && (
              <Icon name={icon} flair="outlined" />
            )}
          </CoreLink>
        );
      }

      case 'table': {
        const updatedNode: WysiwygFieldDomElement = {
          ...node,
          attribs: {
            ...node.attribs,
            tabindex: 0,
          },
        };

        return (
          <TableRender
            element={convertNodeToElement(updatedNode, index, customElementHandler)}
            {...updatedNode.attribs}
          />
        );
      }

      default: {
        // Recursively go over every child node, so any nested nodes will be handled as well.
        return convertNodeToElement(node, index, customElementHandler);
      }
    }
  }, []);

  return (
    <RKSWysiwygField
      {...props}
      customElementHandler={customElementHandler}
    />
  );
};

export default WysiwygField;
